import { T, Var } from 'gt-react';
import { Button } from '@/components/ui/button';
import type { WorkbookEditor } from './useWorkbookEditor';

/**
 * Saved versions, newest first, each restorable. Says so while they load, when
 * they can't be read, and when there are none yet, rather than showing nothing.
 */
export function WorkbookVersions({
  editor,
  busy,
  canUpdate,
  act,
}: {
  editor: Pick<WorkbookEditor, 'restore'> & {
    versions: Pick<WorkbookEditor['versions'], 'data' | 'isPending' | 'error'>;
  };
  busy: boolean;
  canUpdate: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
}) {
  const { versions } = editor;
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="font-medium">
        <T context="workbook saved versions heading">Saved versions</T>
      </h2>
      {versions.isPending ? (
        <p className="text-sm text-muted-foreground">
          <T context="workbook loading versions">Loading versions…</T>
        </p>
      ) : versions.error ? (
        <p role="alert" className="text-sm text-destructive">
          {versions.error.message}
        </p>
      ) : versions.data?.length ? (
        <ol className="flex flex-col gap-2">
          {versions.data.map((version) => (
            <li
              className="flex items-center justify-between gap-2 rounded border p-2 text-xs"
              key={version.id}
            >
              <span>
                <T context="workbook version label">
                  Version <Var>{version.revision}</Var>
                </T>{' '}
                · {new Date(version.createdAt).toLocaleString()}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={busy || !canUpdate}
                onClick={() => void act(() => editor.restore(version.id))}
              >
                <T context="workbook restore version">Restore version</T>
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="workbook no saved versions">
            No saved versions yet. Save version keeps a copy of this workbook
            you can restore later.
          </T>
        </p>
      )}
    </section>
  );
}
